import type { Chassis, Protocol, Role, Tier } from "@zend/content";
import type { StatBlock } from "../units/stats.js";

export type Side = "attacker" | "defender";

/** Fixed squad slots plus turrets and summoned extras. */
export type CombatSlot = "commander" | "f1" | "f2" | "f3" | "f4" | "s1" | "s2" | "turret" | "extra";
export type SlotKind = "commander" | "fighter" | "support" | "turret";

export type BattleContext = "pack" | "hive" | "boss" | "arena" | "expedition" | "base_attack" | "zone" | "siege" | "nexus";

export interface CombatantInput {
  /** Stable id inside the battle (unit row id, or a synthetic id for zombies and turrets). */
  id: string;
  kind: "unit" | "zombie" | "turret";
  /** Content id: unit.x, zmb.x, or "turret". */
  defId: string;
  slot: CombatSlot;
  level: number;
  stars: number;
  /** Ability levels in content order (standard first, then uniques). */
  abilityLevels: number[];
  /** Final stats after every out-of-battle multiplier; commander hp is replaced in battle (R32). */
  stats: StatBlock;
  /** Starting HP share 1..100 (wear from earlier battles, D16). */
  hpPct: number;
  /** Personal cosmetic HP bonus applied after the commander's fighter-average HP is resolved. */
  commanderHpPct?: number;
  /** Overrides the targeting protocol carried by the unit's command pulse (events, experiments, tests). */
  protocol?: Protocol;
}

export interface SideModifiers {
  /** One-use world revenge, persisted in the input so replay retains the actual damage bonus. */
  revengePct?: number;
  atkPct?: number;
  defPct?: number;
  hpPct?: number;
  /** Multiplies aura strength (Uplink doctrine, OMC set). */
  auraPct?: number;
}

export interface SideInput {
  members: CombatantInput[];
  modifiers?: SideModifiers;
}

export interface StructuresInput {
  wallHp: number;
  turrets: { hp: number; atk: number }[];
  traps: { damage: number; count: number };
}

export interface BattleInput {
  /** Missing/1 retains historical replay rules. New server battles use 2. */
  rulesVersion?: 1 | 2;
  seed: number;
  context: BattleContext;
  attacker: SideInput;
  defender: SideInput;
  structures?: StructuresInput;
  /** Defaults to MAX_TICKS (1 800). */
  maxTicks?: number;
  /** Energy every unit starts with (0..100); events and tests use it, live battles start at 0. */
  startEnergy?: number;
}

export type EventKind = "attack" | "ability" | "damage" | "miss" | "heal" | "shield" | "status" | "death" | "revive" | "summon" | "wall" | "trap" | "end" | "target" | "morale";

/** Compact, hashable event: tick, kind, actor, target, value, extra. */
export interface BattleEvent {
  t: number;
  k: EventKind;
  a?: string;
  b?: string;
  v?: number;
  x?: string;
  /** Version 2 hit explanation, captured from the actual damage calculation. */
  detail?: { hitChance: number; attack: number; defence: number; mitigation: number; chassis: number; critical: boolean; shieldAbsorbed: number; moralePct: number; revengePct?: number };
}

export interface UnitSummary {
  id: string;
  defId: string;
  side: Side;
  slot: CombatSlot;
  damageDealt: number;
  damageTaken: number;
  healing: number;
  kills: number;
  abilitiesUsed: number;
  alive: boolean;
  hpPct: number;
}

export type Winner = Side | "timeout";

export interface BattleResult {
  winner: Side;
  /** How the battle ended: a side lost, or the timeout rule decided. */
  outcome: Winner;
  ticks: number;
  units: UnitSummary[];
  mvp: string | null;
  /** Remaining HP share per side (0..1), used by the arena timeout rule and for display. */
  hpShare: { attacker: number; defender: number };
  structures: { wallAbsorbed: number; turretDamage: number; trapDamage: number };
  hash: string;
  events: BattleEvent[];
}

export interface ResolvedAbility {
  index: number;
  archetype: string;
  kind: "standard" | "active" | "passive" | "pulse" | "ultimate" | "aura" | "protocol";
  targets: "enemy" | "enemies" | "ally" | "allies" | "self" | "rear" | "none";
  params: Record<string, number>;
  level: number;
  /** Effect multiplier from the ability level (+8 % per level). */
  magnitude: number;
  usedOnce: boolean;
  /** Ultimates recharge for ULTIMATE_COOLDOWN_TICKS after use so actives get their turn. */
  readyAtTick: number;
}

export interface CombatantMeta {
  role: Role;
  chassis: Chassis | null;
  tier: Tier;
}

export const TICK_MS = 100;
export const MAX_TICKS = 1800;
export const ENERGY_MAX = 100;
export const ENERGY_ON_HIT = 10;
export const CHASSIS_BONUS = 1.1;
export const ULTIMATE_COOLDOWN_TICKS = 150;
